import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import {
    DataGrid,
    Column,
    ColumnChooser,
    ColumnChooserSearch,
    Editing,
    Item,
    Lookup,
    Scrolling,
    StateStoring,
    Toolbar,
    LoadPanel as GridLoadPanel,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react";
import TextArea from "devextreme-react/text-area";
import { alert, confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import { CONCURRENCY_HANDLED, getApiErrorMessage } from "../../../../../common/concurrency";
import { GridLayout } from "../../../../../common/GridLayout";
import GridLayoutManager from "../../../../../gridLayoutCustomization/GridLayoutManager";

/*
 * Legacy uOMD0018 / uOMD0040 opSelectSerialNumberRecords -> Argos.Insight.Win.OM.Views
 * SplitOrderSerialNumberSelectionGrid. The Delphi form handed the order's RowId to the V9 grid, which
 * listed every serial shipped against the order and let the user tick the ones to hold back. Accept
 * (PrepareSerialNumberForSplit) moves the ticked serials to STATUS Split and releases the rest back to
 * Active.
 *
 * The grid is editable, by product decision: the V9 form removed Save from its navbar so its cell edits
 * were discarded, whereas here every column backed by a serial-number field is saved to the database with
 * the selection. Item and lot are the exception - they are joined in from the item/lot masters, so pointing
 * a serial at a different item or lot is a separate operation rather than a cell edit.
 */

const yesNoItems = [
    { ID: "Y", Text: "Yes" },
    { ID: "N", Text: "No" },
];

/* Legacy CalcEdit columns show 2 decimals, in the editor as well as the cell. */
const numberFormat = { type: "fixedPoint", precision: 2 };
const numberEditorOptions = { format: numberFormat };

/* CLAUDE.md rule 9: COMMENT is long text, so it gets a multiline editor. DataGrid has no built-in
   multiline column editor - a template is the supported way to swap the one-line box out. */
const renderCommentEditor = (cell) => (
    <TextArea
        defaultValue={cell.value}
        height={90}
        onValueChanged={(e) => cell.setValue(e.value)}
    />
);

/* Persisted column layout for this grid (custom StateStoring + GridLayoutManager, mirrors
   DetailsTab.js). Replaces the V9 form's Option > Save Layout / UserInterfaceSettingsManager. */
const GRID_KEY = "6B1D74A2-0C58-4E37-9F26-3A5D8E71C4B0";

function SplitOrderSerialNumberSelectionPopup(props) {
    const { showPopup = {}, setShowPopup, oidc } = props;
    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;
    const accessToken = oidc?.user?.access_token;

    const dataGridRef = useRef(null);
    const rowsRef = useRef([]);
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const loadState = useCallback(
        () => GridLayout.sendStorageRequest(GRID_KEY, "GET", accessToken, null, currentLayout),
        [accessToken, currentLayout]
    );
    const saveState = useCallback(
        (state) => GridLayout.sendStorageRequest(GRID_KEY, "POST", accessToken, state, currentLayout),
        [accessToken, currentLayout]
    );

    const closePopup = useCallback(() => {
        rowsRef.current = [];
        setRows([]);
        setLoaded(false);
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    useEffect(() => {
        if (!visible || !headerRowId || !accessToken) return undefined;
        let cancelled = false;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetSplitOrderSerialNumbers}`, {
                params: { headerRowId },
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => {
                if (cancelled) return;
                const payload = res.data || {};
                /* Legacy InitializeData closes the form on a non-Invoice order or an order with no serials. */
                if (payload.Success === false || !payload.Resultdata) {
                    alert(payload.Message || MESSAGES.ERROR, MESSAGES.ERROR).then(closePopup);
                    return;
                }
                const list = payload.Resultdata;
                rowsRef.current = list;
                setRows(list);
                setLoaded(true);
            })
            .catch((err) => {
                if (cancelled) return;
                alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR).then(closePopup);
            });
        return () => {
            cancelled = true;
        };
    }, [visible, headerRowId, accessToken, closePopup]);

    /* Tick edits mutate the bound rows in place, so rowsRef always sees the current selection. */
    const handleAccept = useCallback(async () => {
        const grid = dataGridRef.current?.instance();
        if (grid) await grid.saveEditData();
        setSaving(true);
        try {
            /* Every editable column goes back, touched or not - the API writes what it is sent, so a
               field left out of this map would be saved as null. The token is echoed back unchanged so a
               row another user has since changed is rejected rather than overwritten (rule 16). */
            const payloadRows = rowsRef.current.map((row) => ({
                RowId: row.RowId,
                ConcurrencyToken: row.ConcurrencyToken,
                Selected: row.Selected === true,
                SerialNumber: row.SerialNumber,
                OutTransactionNumber: row.OutTransactionNumber,
                OutTransactionDate: row.OutTransactionDate,
                NetWeight: row.NetWeight,
                NetWeight2: row.NetWeight2,
                GrossWeight: row.GrossWeight,
                GrossWeight2: row.GrossWeight2,
                Area1: row.Area1,
                Area2: row.Area2,
                Volume1: row.Volume1,
                Volume2: row.Volume2,
                Size1: row.Size1,
                Size2: row.Size2,
                Code1: row.Code1,
                Code2: row.Code2,
                Code3: row.Code3,
                Reference1: row.Reference1,
                Reference2: row.Reference2,
                Reference3: row.Reference3,
                Reference4: row.Reference4,
                Reference5: row.Reference5,
                ShipmentVerified: row.ShipmentVerified,
                // Not shown on this grid, but the API fills the entity through the shared
                // SerialNumberDTO.FillEntity, which copies it - round-trip it so it is not blanked.
                ReceiptVerified: row.ReceiptVerified,
                DamageFlag: row.DamageFlag,
                DamageDescription: row.DamageDescription,
                Comment: row.Comment,
            }));
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.UpdateSplitOrderSerialNumbers}`,
                payloadRows,
                {
                    params: { headerRowId },
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            const payload = res.data || {};
            if (payload.Success === false) {
                alert(payload.Message || MESSAGES.ERROR, MESSAGES.ERROR);
                return;
            }
            notify({ message: MESSAGES.SPLIT_ORDER_SERIAL_NUMBERS_APPLIED, width: 280 }, "success", 3000);
            closePopup();
        } catch (err) {
            /* A 409 is already on screen as the shared "record changed by another user" dialog,
               raised by the AppContainer axios interceptor. */
            if (err?.[CONCURRENCY_HANDLED]) return;
            alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
        } finally {
            setSaving(false);
        }
    }, [headerRowId, accessToken, closePopup]);

    /* Legacy cancelButton_Click: warn while the grid holds rows, because the ticks and cell edits are
       only in memory until Accept. Cancelling here just drops them - the order's existing holds are
       released by the Split Order dialog's own Cancel (SQLSTR_CANCELSERIALNUMBERSPLIT), as in uOMD0018. */
    const handleCancel = useCallback(() => {
        if (rowsRef.current.length > 0) {
            confirm(
                `<i>${MESSAGES.SPLIT_ORDER_SERIAL_NUMBERS_CANCEL_CONFIRM}</i>`,
                MESSAGES.SPLIT_ORDER_SERIAL_NUMBERS_CANCEL_TITLE
            ).then((dialogResult) => {
                if (dialogResult) closePopup();
            });
            return;
        }
        closePopup();
    }, [closePopup]);

    const handleHiding = useCallback(
        (e) => {
            e.cancel = true;
            handleCancel();
        },
        [handleCancel]
    );

    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={handleHiding}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={MESSAGES.SPLIT_ORDER_SERIAL_NUMBERS_TITLE}
            container="#root"
            width="85vw"
            height="85vh"
        >
            <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: "4px 8px" }}>
                <div style={{ flex: "1 1 auto", minHeight: 0 }}>
                    <DataGrid
                        ref={dataGridRef}
                        dataSource={rows}
                        keyExpr="RowId"
                        showBorders={true}
                        columnAutoWidth={true}
                        allowColumnResizing={true}
                        allowColumnReordering={true}
                        rowAlternationEnabled={true}
                        height="100%"
                    >
                        <Scrolling mode="virtual" />
                        <GridLoadPanel enabled={true} />
                        <Editing mode="cell" allowUpdating={true} useIcons={true} />
                        <ColumnChooser
                            enabled={true}
                            title="Choose Column"
                            width={290}
                            height={290}
                            sortOrder="asc"
                        >
                            <ColumnChooserSearch
                                enabled={true}
                                editorOptions={{ placeholder: "Search column" }}
                            />
                        </ColumnChooser>
                        <StateStoring
                            enabled={true}
                            type="custom"
                            customLoad={loadState}
                            storageKey={GRID_KEY}
                            customSave={saveState}
                        />
                        <Toolbar>
                            <Item location="after">
                                <GridLayoutManager
                                    gridKey={GRID_KEY}
                                    dataGrid={dataGridRef}
                                    setCurrentLayout={setCurrentLayout}
                                />
                            </Item>
                            <Item name="columnChooserButton" location="after" />
                        </Toolbar>
                        {/* Column set and default visibility mirror the V9 grid, which auto-generates
                            its columns from SerialNumberShipmentArgs: Selected, Serial Number, Item, Lot,
                            Net Weight, Size 1, Area 1 and Volume 1 are shown, the rest ship hidden and are
                            reachable through the column chooser (the V9 "Customization" panel). */}
                        <Column dataField="RowId" visible={false} showInColumnChooser={false} allowEditing={false} />
                        <Column dataField="Selected" caption="Selected" dataType="boolean" width={80} />
                        {/* Record # (rule 10) - KEYSEQUENCE, the column the legacy grid was ordered by. */}
                        <Column dataField="RecordNumber" caption="Record #" width={90} allowEditing={false} />
                        <Column dataField="SerialNumber" caption="Serial Number" />
                        {/* Read-only: joined in from the item and lot masters, not fields of the serial. */}
                        <Column dataField="ItemCode" caption="Item" allowEditing={false} />
                        <Column dataField="LotCode" caption="Lot" allowEditing={false} />
                        <Column dataField="NetWeight" caption="Net Weight" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                        <Column dataField="Size1" caption="Size 1" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                        <Column dataField="Area1" caption="Area 1" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                        <Column dataField="Volume1" caption="Volume 1" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                        <Column dataField="NetWeight2" caption="Net Weight 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="GrossWeight" caption="Gross Weight" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="GrossWeight2" caption="Gross Weight 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Area2" caption="Area 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Volume2" caption="Volume 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Size2" caption="Size 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Code1" caption="Code 1" visible={false} />
                        <Column dataField="Code2" caption="Code 2" visible={false} />
                        <Column dataField="Code3" caption="Code 3" visible={false} />
                        <Column dataField="Reference1" caption="Reference 1" visible={false} />
                        <Column dataField="Reference2" caption="Reference 2" visible={false} />
                        <Column dataField="Reference3" caption="Reference 3" visible={false} />
                        <Column dataField="Reference4" caption="Reference 4" visible={false} />
                        <Column dataField="Reference5" caption="Reference 5" visible={false} />
                        {/* allowClearing: these can legitimately be blank in the database, so the editor has
                            to offer a way back to blank once the user has opened it. */}
                        <Column dataField="ShipmentVerified" caption="Shipment Verified" visible={false}>
                            <Lookup dataSource={yesNoItems} valueExpr="ID" displayExpr="Text" allowClearing={true} />
                        </Column>
                        <Column dataField="DamageFlag" caption="Damage Flag" visible={false}>
                            <Lookup dataSource={yesNoItems} valueExpr="ID" displayExpr="Text" allowClearing={true} />
                        </Column>
                        <Column dataField="DamageDescription" caption="Damage Description" visible={false} />
                        <Column dataField="Comment" caption="Comment" visible={false} editCellTemplate={renderCommentEditor} />
                        {/* Read-only: joined in from the item and lot masters, not fields of the serial. */}
                        <Column dataField="ItemDescription" caption="Item Description" visible={false} allowEditing={false} />
                        <Column dataField="LotDescription" caption="Lot Description" visible={false} allowEditing={false} />
                        <Column dataField="OutTransactionNumber" caption="Out Transaction #" visible={false} />
                        <Column dataField="OutTransactionDate" caption="Out Transaction Date" dataType="date" visible={false} />
                    </DataGrid>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        paddingTop: 10,
                        borderTop: "1px solid #e0e0e0",
                        flexShrink: 0,
                    }}
                >
                    <Button
                        text="Accept"
                        type="default"
                        stylingMode="contained"
                        disabled={saving || !loaded}
                        onClick={handleAccept}
                    />
                    <Button text="Cancel" disabled={saving} onClick={handleCancel} />
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(SplitOrderSerialNumberSelectionPopup);
